/*
 * exclusive30-price.js
 *
 * On product and collection pages, prices for products eligible for the EXCLUSIVE30
 * code render with their normal (metafield) discount, because Liquid cannot read the
 * URL's ?discount parameter. When a shopper arrives with ?discount=EXCLUSIVE30 (the
 * link that also auto-applies the 30% code to their cart), this script swaps the
 * displayed price to the 30%-off value that Liquid precomputed into
 * data-exclusive30-price, so the page matches what the cart will charge. Without the
 * parameter it does nothing, and if anything is missing it leaves the normal price in
 * place.
 *
 * Eligibility (which products) is decided in Liquid from custom.exclusive30_discount;
 * the price value is computed and money-formatted in Liquid. This script never does
 * price arithmetic — it only shows the precomputed value when the code is in the URL.
 */
(function () {
  'use strict';

  var CODE = 'EXCLUSIVE30';

  // Return the discount code in the query string, upper-cased ('' if none).
  function readDiscountCode(search) {
    return (new URLSearchParams(search || '').get('discount') || '')
      .trim()
      .toUpperCase();
  }

  // True only when the query string carries exactly the EXCLUSIVE30 code.
  function isExclusive30(search) {
    return readDiscountCode(search) === CODE;
  }

  // True when the `discount_code` cookie (set by Shopify once the code is applied to
  // the session) contains EXCLUSIVE30. This is what keeps the 30% showing after the
  // shopper navigates away from the ?discount= link: the code lives in the cookie, not
  // the URL. The cookie may hold multiple comma-separated codes. Pure over a raw
  // cookie string so it can be unit-tested.
  function cookieHasExclusive30(cookieString) {
    var match = (cookieString || '').match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!match) return false;
    var value;
    try { value = decodeURIComponent(match[1]); } catch (e) { value = match[1]; }
    return value.split(',').some(function (code) {
      return code.trim().toUpperCase() === CODE;
    });
  }

  // The discount is "active" for display when it's in the URL or already applied.
  function isDiscountActive() {
    return isExclusive30(window.location.search) || cookieHasExclusive30(document.cookie);
  }

  // Replace the first "NN%" token in a badge label with "30%"; leave labels that
  // carry no percentage (e.g. "Bundle") untouched so authored copy isn't mangled.
  function rewriteBadgePercent(text) {
    if (!text) return text;
    return /\d+\s*%/.test(text) ? text.replace(/\d+\s*%/, '30%') : text;
  }

  // Swap the visible discounted price for its precomputed 30% value.
  function applyToPriceElement(priceEl) {
    // A bundle's displayed price is the JS-summed total of its child variants, which the
    // server never sees, so the precomputed attribute here (the bundle product's own
    // price) is wrong for it. Leave bundle prices to component-products-bundle.js, which
    // is triggered to recompute in run().
    if (priceEl.closest('hh-products-bundle')) return;

    var value = priceEl.getAttribute('data-exclusive30-price');
    if (!value) return;

    var ins = priceEl.querySelector('ins');
    if (ins) {
      ins.textContent = value;
    } else {
      // No existing discount markup: turn the plain price into original + 30%.
      var span = priceEl.querySelector('.price__ml');
      if (span) {
        var del = document.createElement('del');
        del.className = 'price__ml';
        del.textContent = span.textContent.trim();
        var newIns = document.createElement('ins');
        newIns.textContent = value;
        span.replaceWith(del, newIns);
      }
    }

    updateBadge(priceEl);
  }

  // If the card shows a discount-percentage badge, bring it in line with 30%.
  // (Product-card only; no-op on product pages, which have no such badge here.)
  function updateBadge(priceEl) {
    var card = priceEl.closest('hh-product-card') || priceEl.closest('.product-card');
    if (!card) return;
    var badge = card.querySelector('.shared__discount-badge .shared__badge-text');
    if (badge) {
      var next = rewriteBadgePercent(badge.textContent);
      if (next !== badge.textContent) badge.textContent = next;
    }
  }

  function run() {
    if (!isDiscountActive()) return;
    var nodes = document.querySelectorAll('[data-exclusive30-price]');
    Array.prototype.forEach.call(nodes, applyToPriceElement);
    // Bundles compute their combined price themselves; now that the code is known to be
    // active, tell them to recompute so the initial paint is 30% off the summed total
    // (component-products-bundle.js listens for this and is exclusive30-aware).
    if (document.querySelector('hh-products-bundle')) {
      document.dispatchEvent(new Event('bundle-price::change'));
    }
  }

  // Expose pure helpers for unit testing under Node.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      isExclusive30: isExclusive30,
      cookieHasExclusive30: cookieHasExclusive30,
      rewriteBadgePercent: rewriteBadgePercent
    };
  }

  // Run in the browser only. Execute as soon as the DOM is parsed to minimize the
  // flash of the normal price; never hide prices up front, so a script failure still
  // leaves the (correct) normal pricing visible. Also re-run after full load:
  // collection cards can live in a carousel that clones slides, and re-running is safe
  // because swapping the price to the same precomputed value is idempotent.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Let other scripts (e.g. the variant selector, which re-renders the price on
    // variant change) ask whether the 30% should currently apply.
    window.exclusive30IsActive = isDiscountActive;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
    window.addEventListener('load', run);
  }
})();
